/**
 * Application store.
 *
 * Combines domain-specific slices into a unified store with persist middleware.
 * Each slice is responsible for its own state and actions.
 *
 * @see ./slices/ for individual slice implementations
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Import slice creators
import type { ModelCategory } from '@/services/api';

import {
  createAuthSlice,
  createSettingsSlice,
  createMessagesSlice,
  createConversationsSlice,
  createRemindersSlice,
  createObserversSlice,
  createLLMProvidersSlice,
  createModelCategoriesSlice,
  createUISlice,
  type AuthSlice,
  type SettingsSlice,
  type MessagesSlice,
  type ConversationsSlice,
  type AgendaEventsSlice,
  type ObserversSlice,
  type LLMProvidersSlice,
  type ModelCategoriesSlice,
  type UISlice,
} from './slices';

import type { LLMProvider } from './types';

// Re-export types for external use
export type {
  LLMProvider,
  Message,
  MessageArtifact,
  Conversation,
  AgendaEvent,
  PaginationState,
} from './types';

export type { PendingMessage } from './slices/messagesSlice';

/**
 * Combined application state type.
 * Union of all slice interfaces.
 */
export type AppState = AuthSlice &
  SettingsSlice &
  MessagesSlice &
  ConversationsSlice &
  AgendaEventsSlice &
  ObserversSlice &
  LLMProvidersSlice &
  ModelCategoriesSlice &
  UISlice;

/**
 * Main application store.
 * Combines all slices with persistence for essential data.
 */
export const useAppStore = create<AppState>()(
  persist(
    (...args) => ({
      ...createAuthSlice(...args),
      ...createSettingsSlice(...args),
      ...createMessagesSlice(...args),
      ...createConversationsSlice(...args),
      ...createRemindersSlice(...args),
      ...createObserversSlice(...args),
      ...createLLMProvidersSlice(...args),
      ...createModelCategoriesSlice(...args),
      ...createUISlice(...args),
    }),
    {
      name: 'pa-app-storage',
      // Only persist essential data, not temporary UI state
      partialize: state => ({
        authToken: state.authToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        settings: state.settings,
        isSettingsLoaded: state.isSettingsLoaded,
        lastActiveConversationId: state.lastActiveConversationId,
      }),
    }
  )
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a setting value from the store.
 * Can be called outside React components.
 */
export const getSetting = (key: string, defaultValue: string = ''): string => {
  const state = useAppStore.getState();
  return state.settings[key] || defaultValue;
};

const getPreferredName = (): string => getSetting('preferred_name');
const getLocation = (): string => getSetting('location');
export const getTimezone = (): string => getSetting('timezone');
const getReceiveDailyBrief = (): boolean => getSetting('receive_daily_brief') === 'true';

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Authentication state selector.
 */
export const useAuth = () => {
  const { user, isAuthenticated, authToken, setAuth, setAuthenticated, clearAuth } = useAppStore();

  return {
    user,
    isAuthenticated,
    authToken,
    setAuth,
    setAuthenticated,
    clearAuth,
  };
};

/**
 * Settings state selector with convenience getters.
 */
export const useSettings = () => {
  const { settings, isSettingsLoaded, setSettings, setSetting, setIsSettingsLoaded } =
    useAppStore();

  return {
    settings,
    isSettingsLoaded,
    setSettings,
    setSetting,
    setIsSettingsLoaded,
    getSetting,
    // Legacy getters for backward compatibility. Read from the subscribed
    // `settings`, never `getSetting()`: that reads the store outside React, so
    // the React Compiler memoises it on its constant key and the name goes stale.
    preferredName: settings['preferred_name'] || '',
    location: settings['location'] || '',
    timezone: settings['timezone'] || '',
    receiveDailyBrief: settings['receive_daily_brief'] === 'true',
    getPreferredName,
    getLocation,
    getTimezone,
    getReceiveDailyBrief,
  };
};

/**
 * Active conversation and messages selector.
 */
export const useConversation = () => {
  const {
    activeConversation,
    messages,
    isStreaming,
    isLoadingConversation,
    lastActiveConversationId,
    pendingMessage,
    contextOverflowConversationIds,
    compactingConversationId,
    setActiveConversation,
    setMessages,
    addMessage,
    insertMessageBefore,
    updateMessage,
    appendToMessage,
    appendHtmlEvent,
    updateActivity,
    removeMessage,
    clearMessages,
    setIsStreaming,
    setIsLoadingConversation,
    setPendingMessage,
    addContextOverflow,
    clearContextOverflow,
    setCompacting,
  } = useAppStore();

  return {
    activeConversation,
    messages,
    isStreaming,
    isLoadingConversation,
    lastActiveConversationId,
    pendingMessage,
    contextOverflowConversationIds,
    compactingConversationId,
    setActiveConversation,
    setMessages,
    addMessage,
    insertMessageBefore,
    updateMessage,
    appendToMessage,
    appendHtmlEvent,
    updateActivity,
    removeMessage,
    clearMessages,
    setIsStreaming,
    setIsLoadingConversation,
    setPendingMessage,
    addContextOverflow,
    clearContextOverflow,
    setCompacting,
  };
};

/**
 * Conversations list selector (sidebar).
 */
export const useConversations = () => {
  const {
    conversations,
    isLoadingConversations,
    conversationsPagination,
    setConversations,
    addConversation,
    updateConversation,
    removeConversation,
    setIsLoadingConversations,
    setConversationsPagination,
  } = useAppStore();

  return {
    conversations,
    isLoadingConversations,
    pagination: conversationsPagination,
    setConversations,
    addConversation,
    updateConversation,
    removeConversation,
    setIsLoadingConversations,
    setPagination: setConversationsPagination,
  };
};

/**
 * UI state selector.
 */
export const useUI = () => {
  const {
    isLoading,
    error,
    homeRoute,
    setHomeRoute,
    setLoading,
    setError,
    isSidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
  } = useAppStore();

  return {
    isLoading,
    error,
    homeRoute,
    setHomeRoute,
    setLoading,
    setError,
    isSidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
  };
};

/**
 * Reminders state selector.
 */
export const useAgendaEvents = () => {
  const {
    reminders,
    completedReminders,
    isLoadingReminders,
    remindersError,
    remindersCapped,
    setReminders,
    setCompletedReminders,
    addReminder,
    updateReminder,
    removeReminder,
    setIsLoadingReminders,
    setRemindersError,
    loadReminders,
  } = useAppStore();

  return {
    reminders,
    completedReminders,
    isLoading: isLoadingReminders,
    error: remindersError,
    capped: remindersCapped,
    setReminders,
    setCompletedReminders,
    addReminder,
    updateReminder,
    removeReminder,
    setIsLoading: setIsLoadingReminders,
    setError: setRemindersError,
    loadReminders,
  };
};

/**
 * Observers state selector.
 */
export const useObservers = () => {
  const { observers, isLoadingObservers, observersError, setObserversError } = useAppStore();

  return {
    observers,
    isLoading: isLoadingObservers,
    error: observersError,
    setObserversError,
  };
};

/** Bucket key + label used for providers with no known category. */
const OTHER_KEY = 'other';
const OTHER_LABEL = 'Other';

/** One category bucket of models for grouped pickers. */
export interface ProviderCategoryGroup {
  key: string;
  label: string;
  models: LLMProvider[];
}

/** Options controlling how {@link groupProvidersByCategory} builds its groups. */
export interface GroupProvidersByCategoryOptions {
  /**
   * Set when the categories fetch failed. Degrades gracefully by grouping on
   * each provider's raw `category` key (used as the label) instead of
   * collapsing everything into a single "Other" bucket.
   */
  error?: boolean;
}

/** Stable within-group ordering: `sortOrder` ascending, then `name`. */
function sortModels(models: LLMProvider[]): LLMProvider[] {
  return [...models].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)
  );
}

/** Bucket providers by a caller-supplied key resolver, preserving input order. */
function bucketProviders(
  providers: LLMProvider[],
  keyFor: (provider: LLMProvider) => string
): Map<string, LLMProvider[]> {
  const buckets = new Map<string, LLMProvider[]>();
  for (const provider of providers) {
    const key = keyFor(provider);
    const existing = buckets.get(key);
    if (existing) existing.push(provider);
    else buckets.set(key, [provider]);
  }
  return buckets;
}

/**
 * Group providers using the fetched category list.
 *
 * Emits one group per category that actually has providers, in the categories
 * array order (already sorted by sortOrder,key), labelled from the category.
 * Providers whose `category` is null or not a known key fall into "Other",
 * rendered last.
 */
function groupByCategories(
  providers: LLMProvider[],
  categories: ModelCategory[]
): ProviderCategoryGroup[] {
  const labelByKey = new Map(categories.map(c => [c.key, c.label]));
  const buckets = bucketProviders(providers, provider =>
    typeof provider.category === 'string' && labelByKey.has(provider.category)
      ? provider.category
      : OTHER_KEY
  );

  const groups: ProviderCategoryGroup[] = [];
  for (const cat of categories) {
    const models = buckets.get(cat.key);
    if (models) groups.push({ key: cat.key, label: cat.label, models: sortModels(models) });
  }
  const other = buckets.get(OTHER_KEY);
  if (other) groups.push({ key: OTHER_KEY, label: OTHER_LABEL, models: sortModels(other) });
  return groups;
}

/**
 * Degraded grouping used when the categories fetch failed: group by the
 * provider's raw `category` key (which also serves as the label), ordered
 * alphabetically by key, with null-category providers under "Other" last.
 */
function groupByRawKey(providers: LLMProvider[]): ProviderCategoryGroup[] {
  const buckets = bucketProviders(providers, provider =>
    typeof provider.category === 'string' && provider.category ? provider.category : OTHER_KEY
  );

  const groups: ProviderCategoryGroup[] = [...buckets.keys()]
    .filter(key => key !== OTHER_KEY)
    .sort((a, b) => a.localeCompare(b))
    .map(key => ({ key, label: key, models: sortModels(buckets.get(key)!) }));
  const other = buckets.get(OTHER_KEY);
  if (other) groups.push({ key: OTHER_KEY, label: OTHER_LABEL, models: sortModels(other) });
  return groups;
}

/**
 * Group providers into ordered display buckets from the data-driven category
 * list (via the `category` column, set in the DB).
 *
 * When categories are present, group order and labels come from the fetched
 * list; an empty list sends every provider to "Other". When `error` is set the
 * grouping degrades to the provider's raw category key so a failed fetch still
 * yields meaningful buckets. Within each group, models keep `sortOrder`,`name`
 * order.
 */
export function groupProvidersByCategory(
  providers: LLMProvider[],
  categories: ModelCategory[],
  { error = false }: GroupProvidersByCategoryOptions = {}
): ProviderCategoryGroup[] {
  return error ? groupByRawKey(providers) : groupByCategories(providers, categories);
}

/**
 * Model categories state selector.
 *
 * Exposes the fetched category list plus its load/error flags and the loader,
 * so grouped pickers can build their labels/order from live data.
 */
export const useModelCategories = () => {
  const { categories, categoriesLoaded, categoriesError, loadCategories } = useAppStore();

  return {
    categories,
    categoriesLoaded,
    categoriesError,
    loadCategories,
  };
};

/**
 * LLM Providers state selector.
 */
export const useLLMProviders = () => {
  const {
    llmProviders,
    isLLMProvidersLoaded,
    llmProvidersError,
    setLLMProviders,
    setIsLLMProvidersLoaded,
    setLLMProvidersError,
    loadLLMProviders,
    categories,
    categoriesError,
  } = useAppStore();

  return {
    llmProviders,
    isLLMProvidersLoaded,
    llmProvidersError,
    setLLMProviders,
    setIsLLMProvidersLoaded,
    setLLMProvidersError,
    loadLLMProviders,
    // Convenience getters
    defaultProvider: llmProviders.find(p => p.isDefault) || null,
    providersByCategory: groupProvidersByCategory(llmProviders, categories, {
      error: categoriesError,
    }),
  };
};
